import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TimeSeries, StockFilter } from '../../lib/types';
import { SalesFilter } from '../SalesFilter';

interface RevenueChartProps {
  data: TimeSeries[];
  salesFilter: StockFilter;
  onSalesFilterChange: (filter: StockFilter) => void;
  operators?: Array<{ id: string; name: string }>;
  machines?: Array<{ id: string; name: string }>;
  products?: Array<{ id: string; name: string }>;
  granularity: 'day' | 'week' | 'month' | 'year';
  onGranularityChange: (granularity: 'day' | 'week' | 'month' | 'year') => void;
  className?: string;
}

export function RevenueChart({ 
  data, 
  salesFilter,
  onSalesFilterChange,
  operators = [],
  machines = [],
  products = [],
  granularity,
  onGranularityChange,
  className = '' 
}: RevenueChartProps) {
  const formatXAxis = (tickItem: string) => {
    const date = new Date(tickItem);
    if (granularity === 'year') {
      return date.toLocaleDateString('en-US', { year: 'numeric' });
    } else if (granularity === 'month') {
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } else if (granularity === 'week') {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTooltip = (value: number, name: string) => {
    return [`£${value.toFixed(2)}`, 'Revenue'];
  };

  return (
    <div className={`bg-white p-6 rounded-lg shadow ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Over Time</h3>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="timestamp" 
              tickFormatter={formatXAxis}
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `£${value}`}
            />
            <Tooltip 
              labelFormatter={(value) => new Date(value).toLocaleDateString()}
              formatter={formatTooltip}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                opacity: 1
              }}
              wrapperStyle={{
                outline: 'none'
              }}
            />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke="#10B981" 
              strokeWidth={2}
              dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
              animationDuration={3000}
              animationEasing="ease-out"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Sales Filter */}
      <SalesFilter 
        filter={salesFilter}
        onFilterChange={onSalesFilterChange}
        operators={operators}
        machines={machines}
        products={products}
        granularity={granularity}
        onGranularityChange={onGranularityChange}
      />
    </div>
  );
}
